import { Button, Form, Modal, Table } from "react-bootstrap";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { Claims, DistroTissueSummary, Tissue, TissueTypes } from "../../../../models";
import {
    calculateDeathToPreservation,
    displayDateNoYear,
    getSurgeryTypeColorClass,
} from "../../../../helper-functions";
import { Link, useNavigate } from "react-router-dom";
import { Lists } from "../../../lists";
import { SurgeryThumbnail } from "../../..";
import { fas, Icon } from "../../../../component-library";
import { useTissueQuery } from "../../../../queries";
import { KeyboardEvent, ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { usePermissions } from "../../../../contexts";
import { createPatch, Operation } from "rfc6902";

interface DistributionDashboardTissuesProps {
    tissueSummaries: DistroTissueSummary[];
}

function DroppableCell({ id, disabled, children }: Readonly<{ id: string; disabled: boolean; children: ReactNode }>) {
    const { setNodeRef, isOver } = useDroppable({
        id,
        disabled,
    });

    return (
        <td ref={setNodeRef}>
            <div
                style={{
                    minHeight: "60px",
                    minWidth: "175px",
                    backgroundColor: isOver ? "var(--bs-secondary)" : "white",
                    borderRadius: "6px",
                    border: "1px solid #aaa",
                }}
            >
                {children}
            </div>
        </td>
    );
}

function DraggableOffer({ tissue, index }: Readonly<{ tissue: Tissue; index: number }>) {
    const navigate = useNavigate();
    const offer = tissue.offers?.find((o) => o.tissueOfferStatus?.name === "Offered");

    const { attributes, listeners, setNodeRef, transform } = useDraggable({
        id: `tissue-id-${tissue.id}-offer-${index}`,
        disabled: true,
    });

    const style = transform
        ? {
              transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
          }
        : undefined;

    const handleOfferSelect = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Enter" || e.key === " ") {
            offer && navigate(`/lists/${Lists.SurgeriesUnfilled}/surgeries/${offer.surgery?.id}`);
        }
    };

    if (!offer) return null;

    return (
        <div
            ref={setNodeRef}
            style={{ ...style, borderRadius: "6px" }}
            {...listeners}
            {...attributes}
            className={`p-2 ${getSurgeryTypeColorClass(offer?.surgery) || "bg-white"} ${offer ? "clickable" : ""}`}
            onClick={() => offer && navigate(`/lists/none/surgeries/${offer.surgery?.id}`)}
            tabIndex={offer ? 0 : undefined}
            onKeyDown={handleOfferSelect}
        >
            {offer && <SurgeryThumbnail surgery={offer.surgery} surgeon={offer.surgeon} tissue={tissue} />}
        </div>
    );
}

function DraggablePotentialPlacement({
    tissue,
    index,
    onCancelPlacement,
    onCancelPlacementKeyDown,
}: Readonly<{
    tissue: Tissue;
    index: number;
    onCancelPlacement: (tissue: Tissue) => void;
    onCancelPlacementKeyDown: (e: KeyboardEvent<SVGSVGElement>, tissue: Tissue) => void;
}>) {
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const canCreateOffer = hasPermission(Claims.OfferTissue);

    const { attributes, listeners, setNodeRef, transform } = useDraggable({
        id: `tissue-id-${tissue.id}-potential-placement-surgery-id-${tissue.potentialSurgery?.id ?? 0}-${index}`,
        disabled: !tissue.potentialSurgery || !canCreateOffer,
    });

    const style = transform
        ? {
              transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
          }
        : undefined;

    const handlePotentialPlacementSelect = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Enter" || e.key === " ") {
            navigate(`/lists/${Lists.SurgeriesUnfilled}/surgeries/${tissue.potentialSurgery?.id}`);
        }
    };

    if (!tissue.potentialSurgery) return null;

    return (
        <div className={getSurgeryTypeColorClass(tissue.potentialSurgery)} style={{ borderRadius: "6px" }}>
            {canCreateOffer && (
                <div className="d-flex justify-content-end">
                    <Icon
                        icon={fas.faTimes}
                        className={"text-primary clickable"}
                        onClick={() => onCancelPlacement(tissue)}
                        style={{ marginRight: "2px" }}
                        tabIndex={0}
                        onKeyDown={(e) => onCancelPlacementKeyDown(e, tissue)}
                    />
                </div>
            )}
            <div ref={setNodeRef} style={style} {...listeners} {...attributes}>
                <div
                    className="px-2 pb-2"
                    onClick={() =>
                        tissue.potentialSurgeryId
                            ? navigate(`/lists/${Lists.SurgeriesUnfilled}/surgeries/${tissue.potentialSurgeryId}`)
                            : {}
                    }
                    tabIndex={tissue.potentialSurgeryId ? 0 : undefined}
                    onKeyDown={handlePotentialPlacementSelect}
                >
                    <SurgeryThumbnail
                        surgery={tissue.potentialSurgery}
                        surgeon={tissue.potentialSurgery.surgeon}
                        tissue={tissue}
                    />
                </div>
            </div>
        </div>
    );
}

export function DistributionDashboardTissues({ tissueSummaries }: Readonly<DistributionDashboardTissuesProps>) {
    const navigate = useNavigate();
    const { updateDistroCommentMutation, hideTissueMutation, updateWithArgMutation } = useTissueQuery();
    const { hasPermission } = usePermissions();
    const canCreateOffer = hasPermission(Claims.OfferTissue);
    const [comments, setComments] = useState<{ tissueId: number; comment: string }[]>([]);
    const [selectedTissueId, setSelectedTissueId] = useState<number>(0);
    const [showHideTissueModal, setShowHideTissueModal] = useState<boolean>(false);

    const selectedTissue = useMemo(
        () => tissueSummaries.find((t) => t.tissue.id === selectedTissueId),
        [selectedTissueId, tissueSummaries]
    );

    useEffect(() => {
        setComments(tissueSummaries.map((data) => ({ tissueId: data.tissue.id, comment: data.comments })));
    }, [tissueSummaries]);

    const initiateHideTissue = (tissueId: number) => {
        setSelectedTissueId(tissueId);
        setShowHideTissueModal(true);
    };

    const hideTissue = async () => {
        setShowHideTissueModal(false);
        await hideTissueMutation.mutateAsync(selectedTissueId);
        setSelectedTissueId(0);
    };

    const getCommentIsSaved = useCallback(
        (tissueId: number) => {
            const latestComment = comments.find((c) => c.tissueId === tissueId)?.comment;
            const savedComment = tissueSummaries.find((t) => t.tissue.id === tissueId)?.comments;
            if (!latestComment && !savedComment) return true; // strings and nulls are equivalent
            return latestComment === savedComment;
        },
        [comments, tissueSummaries]
    );

    const saveComment = async (tissueId: number, comment: string) => {
        if (getCommentIsSaved(tissueId)) return;
        await updateDistroCommentMutation.mutateAsync({ tissueId, comment });
    };

    const getD2P = (data: DistroTissueSummary) => {
        const dToP = calculateDeathToPreservation(data.timeOfDeath, data.recoveryFormData.data);
        return dToP ? `${dToP.hours}h ${dToP.minutes}m` : "";
    };

    const cancelPotentialPlacement = (tissue: Tissue) => {
        if (!tissue.potentialSurgery || !canCreateOffer) return;
        const updatedTissue = {
            ...tissue,
            potentialSurgeryId: undefined,
        };
        const operations: Operation[] = createPatch(tissue, updatedTissue);
        updateWithArgMutation.mutateAsync({ tissueId: tissue.id, data: operations });
    };

    const handlePotentialPlacementCancelSelect = (e: KeyboardEvent<SVGSVGElement>, tissue: Tissue) => {
        if (e.key === "Enter" || e.key === " ") {
            cancelPotentialPlacement(tissue);
        }
    };

    const isOverdue = (data: DistroTissueSummary) => {
        switch (data.tissue.tissueType?.name) {
            case TissueTypes.Sclera:
            case TissueTypes.Sclera1:
            case TissueTypes.Sclera2:
            case TissueTypes.Quarter1:
            case TissueTypes.Quarter2:
            case TissueTypes.Quarter3:
            case TissueTypes.Quarter4:
                return data.daysOut > 364;
            default:
                return data.daysOut > 11;
        }
    };

    return (
        <>
            <div className="distro-tissues-lg ps-3">
                {tissueSummaries.length ? (
                    <Table>
                        <thead>
                            <tr className="distribution-header-row">
                                <td>Tissue</td>
                                <td>DOD</td>
                                <td>D2P</td>
                                <td>Days Out</td>
                                <td>Age</td>
                                <td>Suitability</td>
                                <td>Cell Ct</td>
                                <td>Rim</td>
                                <td>CZ</td>
                                <td>EndoCZ</td>
                                <td>Status</td>
                                <td>Tissue Location</td>
                                <td>Order</td>
                                <td>Offered</td>
                                <td>Potential Placement</td>
                                <td>
                                    Comments{" "}
                                    <Icon
                                        icon={fas.faInfoCircle}
                                        title="Save a comment by clicking or tabbing outside the comment box"
                                        aria-label="Save a comment by clicking or tabbing outside the comment box"
                                        inverse
                                    />
                                </td>
                            </tr>
                        </thead>

                        <tbody>
                            {tissueSummaries.map((data, index) => (
                                <tr
                                    key={data.tissue.id}
                                    className={`distribution-row ${isOverdue(data) ? "danger" : ""}`}
                                >
                                    <td style={{ whiteSpace: "nowrap" }}>
                                        <Link
                                            to={`/lists/${data.status === "Pending" ? "none" : Lists.TissuesCleared}/tissues/${data.tissue.id}/placement`}
                                        >
                                            {data.tissue.number}
                                        </Link>
                                        {isOverdue(data) && (
                                            <div className="d-flex justify-content-center mt-1">
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => initiateHideTissue(data.tissue.id)}
                                                >
                                                    <Icon icon={fas.faTimes} /> Remove
                                                </Button>
                                            </div>
                                        )}
                                    </td>
                                    <td>{displayDateNoYear(data.timeOfDeath)}</td>
                                    <td>{getD2P(data)}</td>
                                    <td>{data.daysOut}</td>
                                    <td>{data.donorAge}</td>
                                    <td>{data.tissueSuitability}</td>
                                    <td>{data.cellCount}</td>
                                    <td style={{ whiteSpace: "nowrap" }}>{data.rim}</td>
                                    <td>{data.clearZone}</td>
                                    <td>{data.endoClearZone}</td>
                                    <td>{data.status}</td>
                                    <td>
                                        {data.tissueLocation || (
                                            <div className="text-center">
                                                <Icon
                                                    icon={fas.faTruckFast}
                                                    className="text-primary"
                                                    style={{ fontSize: "18px" }}
                                                    fade
                                                />
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        {data.orderNumber ? (
                                            <Link to={`/lists/none/orders/${data.orderNumber}`}>
                                                {data.orderNumber}
                                            </Link>
                                        ) : (
                                            ""
                                        )}
                                    </td>

                                    <DroppableCell
                                        id={`tissue-id-${data.tissue.id}-offer-cell-${index}`}
                                        disabled={
                                            !!data.tissue.offers?.find(
                                                (o) => o.tissueOfferStatus?.name === "Offered"
                                            ) || !canCreateOffer
                                        }
                                    >
                                        <DraggableOffer tissue={data.tissue} index={index} />
                                    </DroppableCell>

                                    <DroppableCell
                                        id={`tissue-id-${data.tissue.id}-potential-placement-cell-${index}`}
                                        disabled={
                                            !!data.tissue.potentialSurgery ||
                                            !!data.tissue.offers?.find(
                                                (o) => o.tissueOfferStatus?.name === "Offered"
                                            ) ||
                                            !canCreateOffer
                                        }
                                    >
                                        <DraggablePotentialPlacement
                                            tissue={data.tissue}
                                            index={index}
                                            onCancelPlacement={cancelPotentialPlacement}
                                            onCancelPlacementKeyDown={handlePotentialPlacementCancelSelect}
                                        />
                                    </DroppableCell>

                                    <td>
                                        <Form.Control
                                            as="textarea"
                                            value={comments.find((c) => c.tissueId === data.tissue.id)?.comment}
                                            onChange={(e) =>
                                                setComments((prev) =>
                                                    prev.map((c) =>
                                                        c.tissueId === data.tissue.id
                                                            ? { ...c, comment: e.target.value }
                                                            : c
                                                    )
                                                )
                                            }
                                            onBlur={(e) => saveComment(data.tissue.id, e.target.value)}
                                            style={{
                                                minWidth: "200px",
                                                border: getCommentIsSaved(data.tissue.id) ? "" : "1px solid red",
                                            }}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                ) : (
                    <div>No tissues found.</div>
                )}
            </div>

            <div className="distro-tissues-sm">
                {tissueSummaries.length ? (
                    <div className="bg-primary py-2 my-3">
                        {tissueSummaries.map((data) => (
                            <div
                                key={data.tissue.id}
                                className="tissue-list-item"
                                onClick={() =>
                                    navigate(
                                        `/lists/${data.status === "Pending" ? "none" : Lists.TissuesCleared}/tissues/${data.tissue.id}/placement`
                                    )
                                }
                            >
                                <strong>{data.tissue.number}</strong> DOD: {displayDateNoYear(data.timeOfDeath)},{" "}
                                {data.daysOut} days out. Age: {data.donorAge}. {data.tissueLocation}.{" "}
                                {data.tissueSuitability}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div>No tissues found.</div>
                )}
            </div>

            <Modal show={showHideTissueModal} onHide={() => setShowHideTissueModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Remove Tissue</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to remove tissue {selectedTissue?.tissue.number} from the dashboard?</p>
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-between">
                    <Button variant="secondary" onClick={() => setShowHideTissueModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={hideTissue} disabled={hideTissueMutation.isLoading}>
                        {hideTissueMutation.isLoading ? "Removing..." : "Remove"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
